import type {
  CustomerPageOptions,
  ManagerSidebarMenuItem,
  ManagerTabList,
  ManagerTabTree,
  ManagerTitleList,
} from '@/types/ui'
import { UserRoleCodes } from '@/types'
import type { RouteLocationRaw } from 'vue-router'
/**
 * Главное меню CMS
 * */
export const MANAGER_SIDEBAR_MENU: ManagerSidebarMenuItem[] = [
  {
    title: 'Главная',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    link: { name: 'manager-blocks' },
  },
  {
    title: 'Header & Footer',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    link: { name: 'manager-header-and-footer-footer' },
  },
  {
    title: 'Интернет-магазин',
    roles: [
      UserRoleCodes.Administrator,
      UserRoleCodes.Manager,
      UserRoleCodes.Salesman,
    ],
    children: [
      {
        title: 'Клиенты',
        link: { name: 'manager-clients' },
        roles: [
          UserRoleCodes.Administrator,
          UserRoleCodes.Manager,
          UserRoleCodes.Salesman,
        ],
      },
      {
        title: 'Заказы',
        link: { name: 'manager-orders' },
        roles: [
          UserRoleCodes.Administrator,
          UserRoleCodes.Manager,
          UserRoleCodes.Salesman,
        ],
      },
    ],
  },
  {
    title: 'Товары',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    children: [
      {
        title: 'Товары',
        link: { name: 'manager-products-list' },
        roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
      },
      {
        title: 'Бренды',
        link: { name: 'manager-brands-list' },
        roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
      },
      {
        title: 'Каталоги',
        link: { name: 'manager-catalogs' },
        roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
      },
      {
        title: 'Импорт фото',
        link: { name: 'manager-import-list' },
        roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
      },
    ],
  },
  {
    title: 'Блог',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    link: { name: 'manager-posts' },
  },
  {
    title: 'Сертификаты',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    link: { name: 'manager-giftcards' },
  },
  {
    title: 'Переводы',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    link: { name: 'manager-dictionaries' },
  },
  {
    title: 'Маркетинговые инструменты',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    link: { name: 'manager-marketing-coupons' },
  },
  {
    title: 'Регистрация в системе лояльности',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    link: { name: 'manager-loyalty' },
  },
  {
    title: 'Настройки CMS',
    roles: [UserRoleCodes.Administrator],
    children: [
      {
        title: 'Доступ в CMS',
        link: { name: 'manager-users' },
        roles: [UserRoleCodes.Administrator],
      },
      {
        title: 'Данные из МойСклад',
        link: { name: 'manager-moysklad-brands' },
        roles: [UserRoleCodes.Administrator],
      },
      {
        title: 'Синхронизация с МойСклад',
        link: { name: 'manager-moysklad-sync' },
        roles: [UserRoleCodes.Administrator],
      },
    ],
  },
  {
    title: 'SEO: статичные страницы',
    roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    link: { name: 'manager-seo' },
  },
]
/**
 * Titles (pages with tabs)
 * */
export const MANAGER_TITLE_LIST: ManagerTitleList = {
  /**
   * Главная
   * */
  'manager-blocks': {
    title: 'Главная',
    link: '/',
  },
  /**
   * Каталоги
   * */
  'manager-labels': {
    title: 'Каталоги',
  },
  'manager-filters': {
    title: 'Каталоги',
  },
  'manager-catalogs': {
    title: 'Каталоги',
  },
  /**
   * Товары
   * */
  'manager-products-list': {
    title: 'Товары',
  },
  'manager-products-discounts': {
    title: 'Товары',
  },
  'manager-products-delivery-payment-text': {
    title: 'Товары',
  },
  'manager-products-angles': {
    title: 'Товары',
  },
  'manager-products-you-may-like': {
    title: 'Товары',
  },
  'manager-products-options': {
    title: 'Товары',
  },
  'manager-products-lenses-carousel': {
    title: 'Товары',
  },
  /**
   * Бренды
   * */
  'manager-brands-list': {
    title: 'Бренды',
    link: '/brands',
  },
  /**
   * МойСклад
   * */
  'manager-moysklad-brands': {
    title: 'Данные из МойСклад',
  },
  'manager-moysklad-categories': {
    title: 'Данные из МойСклад',
  },
  /**
   * Header & Footer
   * */
  'manager-header-and-footer-footer': {
    title: 'Header & Footer',
  },
  'manager-header-and-footer-menu': {
    title: 'Header & Footer',
  },
  'manager-header-and-footer-search': {
    title: 'Header & Footer',
  },
  'manager-header-and-footer-account': {
    title: 'Header & Footer',
  },
  /**
   * Маркетинговые инструменты
   * */
  'manager-marketing-coupons': {
    title: 'Маркетинговые инструменты',
  },
  'manager-marketing-ticker': {
    title: 'Маркетинговые инструменты',
  },
  'manager-marketing-popup': {
    title: 'Маркетинговые инструменты',
  },
  'manager-marketing-subscription': {
    title: 'Маркетинговые инструменты',
  },
  'manager-marketing-products': {
    title: 'Маркетинговые инструменты',
  },
  /**
   * Блог / Посты
   * */
  'manager-posts-item-general': {
    title: 'Настройки поста',
    back: { name: 'manager-posts' },
  },
  'manager-posts-item-seo': {
    title: 'Настройки поста',
    back: { name: 'manager-posts' },
  },
  /**
   * Электронные сертификаты
   * */
  'manager-giftcards-item-general': {
    title: 'Настройки сертификата',
    back: { name: 'manager-giftcards' },
  },
  'manager-giftcards-item-seo': {
    title: 'Настройки seo сертификата',
    back: { name: 'manager-giftcards' },
  },
  /**
   * Переводы
   * */
  'manager-dictionaries-item-general': {
    title: 'Настройки перевода',
    back: { name: 'manager-dictionaries' },
  },
  /**
   * SEO
   * */
  'manager-seo': {
    title: 'SEO',
  },
  /**
   * Для useTitle (заголовок в табе браузера)
   * */
  'manager-clients': {
    title: 'Клиенты',
  },
  'manager-clients-item': {
    title: 'Клиент',
  },
  'manager-orders': {
    title: 'Заказы',
  },
  'manager-orders-item': {
    title: 'Заказ',
  },
  'manager-loyalty': {
    title: 'Регистрация в системе лояльности',
  },
  'manager-users': {
    title: 'Доступ в CMS',
  },
  'manager-moysklad-sync': {
    title: 'Синхронизация с МойСклад',
  },
  'manager-posts': {
    title: 'Блог',
  },
  'manager-giftcards': {
    title: 'Подарочные сертификаты',
  },
  'manager-import-list': {
    title: 'Импорт фото',
  },
}
/**
 * Tabs
 * */
export const MANAGER_BLOCK_TAB_LIST: ManagerTabList = {
  'manager-blocks': {
    name: 'Контент',
    link: { name: 'manager-blocks' },
  },
  'manager-seo': {
    name: 'SEO',
    link: { name: 'manager-seo' },
  },
}

export const MANAGER_CATALOG_TAB_LIST: ManagerTabList = {
  'manager-catalogs': {
    name: 'Каталоги',
    link: { name: 'manager-catalogs' },
  },
  'manager-filters': {
    name: 'Фильтры',
    link: { name: 'manager-filters' },
  },
  'manager-labels': {
    name: 'Ярлыки',
    link: { name: 'manager-labels' },
  },
}

export const MANAGER_CATALOG_ITEM_TAB_LIST: ManagerTabList = {
  'manager-catalogs-item-general': {
    name: 'Основное',
    link: (params?) => getLink('manager-catalogs-item-general', params),
  },
  'manager-catalogs-item-seo': {
    name: 'SEO',
    link: (params?) => getLink('manager-catalogs-item-seo', params),
  },
  'manager-catalogs-item-presets': {
    name: 'Кружки-фильтры для шапки',
    link: (params?) => getLink('manager-catalogs-item-presets', params),
  },
  'manager-catalogs-item-single-product': {
    name: 'Единый товар',
    link: (params?) => getLink('manager-catalogs-item-single-product', params),
  },
  'manager-catalogs-item-merchandising': {
    name: 'Мерчендайзинг',
    link: (params?) => getLink('manager-catalogs-item-merchandising', params),
  },
}

export const MANAGER_PRODUCT_TAB_LIST: ManagerTabList = {
  'manager-products-list': {
    name: 'Список товаров',
    link: { name: 'manager-products-list' },
  },
  'manager-products-discounts': {
    name: 'Размер скидок',
    link: { name: 'manager-products-discounts' },
  },
  'manager-products-delivery-payment-text': {
    name: 'Текст меню «Доставка и оплата»',
    link: { name: 'manager-products-delivery-payment-text' },
  },
  'manager-products-angles': {
    name: 'Фото: виды и порядок',
    link: { name: 'manager-products-angles' },
  },
  'manager-products-you-may-like': {
    name: 'Вам может понравиться',
    link: { name: 'manager-products-you-may-like' },
  },
  'manager-products-options': {
    name: 'Порядок «Детали»',
    link: { name: 'manager-products-options' },
  },
  'manager-products-lenses-carousel': {
    name: 'Линзы: товарный фид',
    link: { name: 'manager-products-lenses-carousel' },
  },
}

export const MANAGER_PRODUCT_ITEM_TAB_LIST: ManagerTabList = {
  'manager-products-item-general': {
    name: 'Основное',
    link: (params?) => getLink('manager-products-item-general', params),
  },
  'manager-products-item-properties': {
    name: 'Свойства на «Мой Склад»',
    link: (params?) => getLink('manager-products-item-properties', params),
  },
  'manager-products-item-other': {
    name: 'Другие цвета и Размеры',
    link: (params?) => getLink('manager-products-item-other', params),
  },
  'manager-products-item-seo': {
    name: 'SEO',
    link: (params?) => getLink('manager-products-item-seo', params),
  },
}

export const MANAGER_BRAND_TAB_LIST: ManagerTabList = {
  'manager-brands-list': {
    name: 'Контент',
    link: { name: 'manager-brands-list' },
  },
  'manager-brands-seo': {
    name: 'SEO',
    link: { name: 'manager-seo' },
  },
}

export const MANAGER_BRAND_ITEM_TAB_LIST: ManagerTabList = {
  'manager-brands-item-general': {
    name: 'Контент',
    link: (params?) => getLink('manager-brands-item-general', params),
  },
  'manager-brands-item-seo': {
    name: 'SEO',
    link: (params?) => getLink('manager-brands-item-seo', params),
  },
}

export const MANAGER_MOY_SKLAD_TAB_LIST: ManagerTabList = {
  'manager-moysklad-brands': {
    name: 'Бренды',
    link: { name: 'manager-moysklad-brands' },
  },
  'manager-moysklad-categories': {
    name: 'Категории товаров',
    link: { name: 'manager-moysklad-categories' },
  },
}

export const MANAGER_HEADER_AND_FOOTER_TAB_LIST: ManagerTabList = {
  'manager-header-and-footer-footer': {
    name: 'Footer',
    link: { name: 'manager-header-and-footer-footer' },
  },
  'manager-header-and-footer-menu': {
    name: 'Меню',
    link: { name: 'manager-header-and-footer-menu' },
  },
  'manager-header-and-footer-search': {
    name: 'Поиск',
    link: { name: 'manager-header-and-footer-search' },
  },
  'manager-header-and-footer-account': {
    name: 'Личный кабинет',
    link: { name: 'manager-header-and-footer-account' },
  },
}

export const MANAGER_MARKETING_TAB_LIST: ManagerTabList = {
  'manager-marketing-coupons': {
    name: 'Промокоды',
    link: { name: 'manager-marketing-coupons' },
  },
  'manager-marketing-ticker': {
    name: 'Баннер-полоса',
    link: { name: 'manager-marketing-ticker' },
  },
  'manager-marketing-popup': {
    name: 'Popup-баннер',
    link: { name: 'manager-marketing-popup' },
  },
  'manager-marketing-subscription': {
    name: 'Подписки на email-рассылки',
    link: { name: 'manager-marketing-subscription' },
  },
  'manager-marketing-products': {
    name: 'Уведомить о поступлении товара',
    link: { name: 'manager-marketing-products' },
  },
}

export const MANAGER_POST_ITEM_TAB_LIST: ManagerTabList = {
  'manager-posts-item-general': {
    name: 'Контент',
    link: (params?) => getLink('manager-posts-item-general', params),
  },
  'manager-posts-item-seo': {
    name: 'SEO',
    link: (params?) => getLink('manager-posts-item-seo', params),
  },
}

export const MANAGER_GIFTCARD_ITEM_TAB_LIST: ManagerTabList = {
  'manager-giftcards-item-general': {
    name: 'Контент',
    link: (params?) => getLink('manager-giftcards-item-general', params),
  },
  'manager-giftcards-item-seo': {
    name: 'SEO',
    link: (params?) => getLink('manager-giftcards-item-seo', params),
  },
}

export const MANAGER_DICTIONARIES_ITEM_TAB_LIST: ManagerTabList = {
  'manager-dictionaries-item-general': {
    name: 'Контент',
    link: (params?) => getLink('manager-dictionaries-item-general', params),
  },
}

export const MANAGER_SEO_TAB_LIST: ManagerTabList = {
  'manager-seo': {
    name: 'Статичные страницы',
    link: { name: 'manager-seo' },
  },
}
/**
 * Tabs per page name
 * */
export const MANAGER_TAB_TREE: ManagerTabTree = {
  'manager-blocks': MANAGER_BLOCK_TAB_LIST,
  'manager-labels': MANAGER_CATALOG_TAB_LIST,
  'manager-filters': MANAGER_CATALOG_TAB_LIST,
  'manager-catalogs': MANAGER_CATALOG_TAB_LIST,

  'manager-catalogs-item-general': MANAGER_CATALOG_ITEM_TAB_LIST,
  'manager-catalogs-item-seo': MANAGER_CATALOG_ITEM_TAB_LIST,
  'manager-catalogs-item-presets': MANAGER_CATALOG_ITEM_TAB_LIST,
  'manager-catalogs-item-single-product': MANAGER_CATALOG_ITEM_TAB_LIST,
  'manager-catalogs-item-merchandising': MANAGER_CATALOG_ITEM_TAB_LIST,

  'manager-products-list': MANAGER_PRODUCT_TAB_LIST,
  'manager-products-discounts': MANAGER_PRODUCT_TAB_LIST,
  'manager-products-delivery-payment-text': MANAGER_PRODUCT_TAB_LIST,
  'manager-products-angles': MANAGER_PRODUCT_TAB_LIST,
  'manager-products-you-may-like': MANAGER_PRODUCT_TAB_LIST,
  'manager-products-options': MANAGER_PRODUCT_TAB_LIST,
  'manager-products-lenses-carousel': MANAGER_PRODUCT_TAB_LIST,

  'manager-products-item-general': MANAGER_PRODUCT_ITEM_TAB_LIST,
  'manager-products-item-properties': MANAGER_PRODUCT_ITEM_TAB_LIST,
  'manager-products-item-other': MANAGER_PRODUCT_ITEM_TAB_LIST,
  'manager-products-item-seo': MANAGER_PRODUCT_ITEM_TAB_LIST,

  'manager-brands-list': MANAGER_BRAND_TAB_LIST,

  'manager-brands-item-general': MANAGER_BRAND_ITEM_TAB_LIST,
  'manager-brands-item-seo': MANAGER_BRAND_ITEM_TAB_LIST,

  'manager-moysklad-brands': MANAGER_MOY_SKLAD_TAB_LIST,
  'manager-moysklad-categories': MANAGER_MOY_SKLAD_TAB_LIST,

  'manager-header-and-footer-footer': MANAGER_HEADER_AND_FOOTER_TAB_LIST,
  'manager-header-and-footer-menu': MANAGER_HEADER_AND_FOOTER_TAB_LIST,
  'manager-header-and-footer-search': MANAGER_HEADER_AND_FOOTER_TAB_LIST,
  'manager-header-and-footer-account': MANAGER_HEADER_AND_FOOTER_TAB_LIST,

  'manager-marketing-coupons': MANAGER_MARKETING_TAB_LIST,
  'manager-marketing-ticker': MANAGER_MARKETING_TAB_LIST,
  'manager-marketing-popup': MANAGER_MARKETING_TAB_LIST,
  'manager-marketing-subscription': MANAGER_MARKETING_TAB_LIST,
  'manager-marketing-products': MANAGER_MARKETING_TAB_LIST,

  'manager-posts-item-general': MANAGER_POST_ITEM_TAB_LIST,
  'manager-posts-item-seo': MANAGER_POST_ITEM_TAB_LIST,

  'manager-giftcards-item-general': MANAGER_GIFTCARD_ITEM_TAB_LIST,
  'manager-giftcards-item-seo': MANAGER_GIFTCARD_ITEM_TAB_LIST,

  'manager-dictionaries-item-general': MANAGER_DICTIONARIES_ITEM_TAB_LIST,

  'manager-seo': MANAGER_SEO_TAB_LIST,
}

function getLink(name: string, params?: any) {
  if (!params) return { name }

  return {
    name,
    params,
  }
}
/**
 * Список рутов для настройки меню витрины в CMS
 * */
export const CUSTOMER_ROUTES_OPTIONS: RouteLocationRaw[] = [
  { name: 'customer-home' },
  // { name: 'customer-favorites' },
  { name: 'customer-brands' },
  { name: 'customer-checkout' },
  { name: 'customer-account' },
  { name: 'customer-loyalty' },
  { name: 'customer-blog' },
]

export const CUSTOMER_HELP_PAGES: CustomerPageOptions = [
  { key: 'customer-help-home', title: 'Помощь покупателям' },
  {
    key: 'customer-help-warranty',
    title: 'Гарантия',
    menuName: 'Гарантия',
  },
  {
    key: 'customer-help-delivery',
    title: 'Доставка и оплата',
    menuName: 'Доставка и оплата',
  },
  {
    key: 'customer-help-return',
    title: 'Возврат',
    menuName: 'Возврат',
  },
  {
    key: 'customer-help-repair',
    title: 'Ремонт',
    menuName: 'Ремонт',
  },
  {
    key: 'customer-help-sizes',
    title: 'Размеры',
    menuName: 'Размеры',
  },
  {
    key: 'customer-help-care',
    title: 'Инструкция по уходу за очками',
    menuName: 'Уход за очками',
  },
  {
    key: 'customer-help-loyalty',
    title: 'Программа лояльности',
    menuName: 'Система лояльности',
  },
  {
    key: 'customer-blog',
    title: 'Блог',
    menuName: 'Блог',
  },
]
